import {cn} from '../../lib/utils';
import {Breadcrumbs} from '../Breadcrumbs';
import {BreadcrumbsRow} from '../BreadcrumbsRow';
import {Typography} from '../Typography';

import type {BreadcrumbsProps} from '../Breadcrumbs';
import type {BreadcrumbsRowBack, BreadcrumbsRowForward} from '../BreadcrumbsRow';
import type {ComponentProps, ReactNode} from 'react';

export interface PageHeaderProps extends ComponentProps<'div'> {
  title: string;
  titleNode?: ReactNode;
  description?: ReactNode;
  badge?: ReactNode;
  actions?: ReactNode;
  breadcrumbs?: ReactNode;
  breadcrumbsMetadata?: BreadcrumbsProps;
  back?: BreadcrumbsRowBack;
  forward?: BreadcrumbsRowForward;
  tabs?: ReactNode;
  actionsBesideTabs?: boolean;
}

export function PageHeader({
  title,
  titleNode,
  description,
  badge,
  actions,
  breadcrumbs,
  breadcrumbsMetadata,
  back,
  forward,
  tabs,
  actionsBesideTabs = false,
  className,
  ...props
}: PageHeaderProps) {
  const besideTabs = actionsBesideTabs && Boolean(tabs);

  return (
    <div
      data-slot="page-header"
      className={cn(
        'rounded-tr-4xl px-page pt-5 border-b',
        tabs ? 'pb-0 border-border-subtle' : 'pb-5 border-border',
        className
      )}
      {...props}
    >
      <div
        className={cn('flex justify-between gap-4', besideTabs ? 'items-stretch' : 'items-start')}
      >
        <div className={cn('min-w-0 flex-1', besideTabs && 'flex flex-col')}>
          {(breadcrumbs ?? breadcrumbsMetadata) && (
            <BreadcrumbsRow back={back} forward={forward} className="mb-2">
              {breadcrumbs ?? <Breadcrumbs {...breadcrumbsMetadata!} />}
            </BreadcrumbsRow>
          )}
          <div className="flex flex-col gap-2">
            <div className="flex items-baseline gap-2">
              <h1 className="font-semibold text-fg text-display-sm leading-display-sm font-display">
                {titleNode ?? title}
              </h1>
              {badge && (
                <span
                  className={cn(
                    'text-xs text-fg-tertiary',
                    typeof badge !== 'string' && 'self-center'
                  )}
                >
                  {badge}
                </span>
              )}
            </div>
            {description &&
              (typeof description === 'string' ? (
                <Typography size="sm" color="tertiary">
                  {description}
                </Typography>
              ) : (
                <div>{description}</div>
              ))}
          </div>
          {besideTabs && <div className="mt-auto pt-6">{tabs}</div>}
        </div>
        {actions && (
          <div className={cn('flex shrink-0 items-start', besideTabs && 'pb-3')}>{actions}</div>
        )}
      </div>
      {tabs && !besideTabs && <div className="mt-6">{tabs}</div>}
    </div>
  );
}
